import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { NetworkService, SessionService } from '@movilidad/app-core';
import { AuthService } from '../services/auth.service';
import { ForceUpdateService, STORE_BEHIND_HEADER } from '../services/force-update.service';
import { NotificationService } from '../services/notification/notification.service';

export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);
  const network = inject(NetworkService);
  const injector = inject(Injector);

  const printMessage = (errorMsg: string) => {
    if (!environment.production) {
      notificationService.presentToast(errorMsg);
    }
  };

  // La tienda aún no ofrece la versión mínima (ver ForceUpdateService): el API no debe responder 426.
  const isApi = request.url.includes(environment.apiBaseUrl);
  if (isApi && injector.get(ForceUpdateService).storeBehind) {
    request = request.clone({ setHeaders: { [STORE_BEHIND_HEADER]: '1' } });
  }

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        Sentry.addBreadcrumb({
          category: 'http',
          // Sin query string: puede traer correos, matrículas o tokens (auditoría 2026-10).
          message: `HTTP Error: ${error.status} ${request.method} ${request.url}`,
          data: {
            requestId: request.headers.get('X-Request-ID'),
            status: error.status,
            statusText: error.statusText,
            online: network.isOnline(),
          },
          level: error.status === 0 ? 'warning' : 'error',
        });

        // 426: versión por debajo del mínimo (ver ForceUpdateService). Pantalla de actualizar, sin
        // toast y sin cerrar la sesión; si la tienda confirma que aún no ofrece el mínimo, la misma
        // petición se reintenta una vez con el header. Se resuelve aquí (no al construir el
        // interceptor) porque el servicio usa ApiService, que usa HttpClient.
        if (error.status === 426 && error.error?.force_update) {
          const info = {
            message: error.error.message ?? null,
            updateUrl: error.error.update_url ?? null,
            minVersion: error.error.min_version ?? null,
            strict: error.error.strict === true,
          };
          return from(injector.get(ForceUpdateService).enforce(info)).pipe(
            switchMap((outcome) =>
              outcome === 'continue' && isApi && !request.headers.has(STORE_BEHIND_HEADER)
                ? next(request.clone({ setHeaders: { [STORE_BEHIND_HEADER]: '1' } }))
                : throwError(() => error)
            )
          );
        }

        // 409 `campus_changed` (ADR 0002): el usuario cambió de campus en otro dispositivo y el API no
        // ejecutó la petición. Se relee `me` (la caché se invalida ahí y las queries se vuelven a pedir
        // con el campus nuevo) y el error sigue su curso: una escritura no se reintenta sola, el usuario
        // debe confirmarla en el campus nuevo. Se resuelve aquí por el mismo ciclo que ForceUpdateService.
        if (error.status === 409 && isApi && error.error?.code === 'campus_changed') {
          return from(injector.get(AuthService).resyncCampus()).pipe(switchMap(() => throwError(() => error)));
        }

        // Handle 401 immediately — no toast, no body parsing. SessionService (TEC-293) deja pasar
        // uno cada 2 s aunque fallen varias peticiones a la vez, y llama a `onUnauthorized` de
        // MovilidadSessionHooks: el `user:logout` de siempre. Se resuelve aquí, como
        // ForceUpdateService, para no crear la sesión al construir el interceptor.
        if (error.status === 401) {
          injector.get(SessionService).handleUnauthorized();
          return throwError(() => error);
        }

        let errorMessage = '';
        if (error.error && Object.prototype.hasOwnProperty.call(error.error, 'errors') && error.error.errors.length) {
          error.error.errors.forEach((errorMsg: string) => {
            printMessage(errorMsg);
          });
        } else if (error.error && Array.isArray(error.error.message)) {
          errorMessage += error.error.message[0];
          printMessage(errorMessage);
        } else {
          if (error.status === 408 || error.status === 504) {
            printMessage('Solicitud demorada, intente nuevamente.');
          } else {
            errorMessage += error.error?.message || error.message;
            printMessage(errorMessage);
          }
        }
      }
      return throwError(() => error);
    })
  );
};
